import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReportCardItem } from "winslow-core";
import { Button } from "../../ds2/components";
import {
  apiGetReportCardItemsForCollection,
  apiCreateReportCardItemsForCollection,
} from "../../services/ReportCardService";
import "./CollectionReportCard.css";
import "../../css/Icons.css";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import {
  apiRunReportCardItems,
  apiFlagReportCardItem,
} from "../../services/ReportCardService";
import {
  apiRunReportCardItemsPayload,
  apiFlagReportCardItemPayload,
} from "../../services/ReportCardService.types";
import { iconPath } from "winslow-core";
import { useSelector } from "react-redux";
import {
  selectEditingCollection,
  selectUser,
} from "../../redux/features/appData";
import ModalNoResources from "./ModalNoResources";

type CollectionReportCardProps = {};

export const CollectionReportCard = ({}: CollectionReportCardProps) => {
  const navigate = useNavigate();

  const { collectionId } = useParams();

  const storeUser = useSelector(selectUser);
  const editingCollection = useSelector(selectEditingCollection);

  const [reportCardItems, setReportCardItems] = useState<
    ReportCardItem[] | null
  >(null);
  const [currentReportCardItem, setCurrentReportCardItem] =
    useState<ReportCardItem | null>(null);
  const [reportCard, setReportCard] = useState<Map<string, ReportCardItem[]>>();
  const [categoryOpen, setCategoryOpen] = useState<Map<string, boolean>>();
  const [categorySelected, setCategorySelected] =
    useState<Map<string, boolean>>();
  const [itemSelected, setItemSelected] = useState<boolean[]>([]);
  const [itemFlagged, setItemFlagged] = useState<boolean[]>([]);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState<boolean>(false);

  const [showNoResources, setShowNoResources] = useState<boolean>(false);

  useEffect(() => {
    if (collectionId) {
      (async () => {
        await loadReportCardItems(false);
      })();
    }
  }, [collectionId]);

  const loadReportCardItems = async (refreshItemsOnly: boolean = false) => {
    if (!collectionId) {
      return;
    }

    let items = await apiGetReportCardItemsForCollection(collectionId);

    // if no items, create report card
    if (!items || (items && items.length === 0)) {
      toast("Generating report card questions.", toastSuccess);

      items = await apiCreateReportCardItemsForCollection(collectionId);
    }

    if (items && items.length > 0) {
      // get them in same order all the time
      items.sort((a: ReportCardItem, b: ReportCardItem) => {
        return a.question.toLowerCase().localeCompare(b.question.toLowerCase());
      });

      setReportCardItems(items);

      // update the current item in case its answer has changed
      if (refreshItemsOnly && currentReportCardItem) {
        const index = items.findIndex((item: ReportCardItem) => {
          return item.id === currentReportCardItem.id;
        });

        if (index !== -1) {
          setCurrentReportCardItem({ ...items[index] });
        }

        // clear out selections
      }

      if (!refreshItemsOnly) {
        setCurrentReportCardItem({ ...items[0] });

        const newReportCard: Map<string, ReportCardItem[]> = new Map<
          string,
          ReportCardItem[]
        >();
        const newCategoryOpen: Map<string, boolean> = new Map<
          string,
          boolean
        >();
        const newCategorySelected: Map<string, boolean> = new Map<
          string,
          boolean
        >();
        const newItemSelected: boolean[] = [];
        const newItemFlagged: boolean[] = [];

        // ham fisted but its a small data set
        if (items && items.length > 0) {
          items.forEach((rci: ReportCardItem) => {
            newCategorySelected.set(rci.category, false);
            newCategoryOpen.set(rci.category, false);
            newItemSelected.push(false);
            newItemFlagged.push(rci.flag);
            newReportCard.set(
              rci.category,
              items!.filter((item: ReportCardItem) => {
                return item.category === rci.category;
              })
            );
          });

          setCategoryOpen(newCategoryOpen);
          setCategorySelected(newCategorySelected);
          setItemSelected(newItemSelected);
          setItemFlagged(newItemFlagged); // same array no new one needed
          setReportCard(newReportCard);
        }
      }
    }
  };

  const handleCreateReportCard = async () => {
    if (collectionId) {
      const items = await apiCreateReportCardItemsForCollection(collectionId);

      loadReportCardItems(false);
    }
  };

  const handleCategorySelect = (category: string) => {
    if (!categorySelected || !reportCardItems) {
      return;
    }

    const newCategorySelected: Map<string, boolean> = new Map(categorySelected);
    const current = categorySelected.get(category);
    newCategorySelected.set(category, !current);

    // turn on/off all the sub questions
    const newItemSelected = [...itemSelected];

    for (let i = 0; i < reportCardItems.length; i++) {
      console.log(
        "compare: " + reportCardItems[i].category + " to " + category
      );
      if (reportCardItems[i].category === category) {
        newItemSelected[i] = !current;
      }
    }

    setCategorySelected(newCategorySelected);
    setItemSelected(newItemSelected);
  };

  const handleItemSelect = (index: number) => {
    if (index < 0) {
      return;
    }

    const newItemSelected = [...itemSelected];
    newItemSelected[index] = !itemSelected[index];
    setItemSelected(newItemSelected);
  };

  const handleItemFlag = async (index: number) => {
    if (index < 0 || !reportCardItems) {
      return;
    }

    const newItemFlagged = [...itemFlagged];
    newItemFlagged[index] = !itemFlagged[index];
    setItemFlagged(newItemFlagged);

    const payload: apiFlagReportCardItemPayload = {
      reportCardItemId: reportCardItems[index].id,
      flag: newItemFlagged[index],
    };

    await apiFlagReportCardItem(payload);
    // toast("Flagged for later.", toastSuccess);
  };

  const handleUpdateAll = (state: boolean) => {
    const categories = reportCard
      ? Array.from(reportCard.keys())
      : ([] as string[]);

    if (categories) {
      const newCategorySelected: Map<string, boolean> = new Map<
        string,
        boolean
      >();

      categories.forEach((category: string) => {
        newCategorySelected.set(category, state);
      });

      const newItemSelected = [...itemSelected];

      for (let i = 0; i < newItemSelected.length; i++) {
        newItemSelected[i] = state;
      }

      setCategorySelected(newCategorySelected);
      setItemSelected(newItemSelected);
    }
  };

  const handleRun = async () => {
    if (!reportCardItems) {
      return;
    }

    if (
      editingCollection?.linkRecords &&
      editingCollection.linkRecords.length < 1
    ) {
      setShowNoResources(true);
      return;
    }

    const runItems: string[] = [];

    for (let i = 0; i < itemSelected.length; i++) {
      if (itemSelected[i]) {
        runItems.push(reportCardItems[i].id);
      }
    }

    if (runItems.length === 0) {
      toast("Please select one or more item to run.", toastFail);
      return;
    }

    toast(
      "Running " + runItems.length + " items, this may take a minute.",
      toastSuccess
    );

    const payload: apiRunReportCardItemsPayload = {
      items: runItems,
    };

    const newItems = await apiRunReportCardItems(payload);

    console.log("[CollectionReportCard/handleRun] newItems: ", newItems);

    if (!newItems) {
      toast("Something went wrong, please try again.", toastFail);
      return;
    }

    toast("Report card items are complete.", toastSuccess);
    await loadReportCardItems(true);
  };

  const handleShowFlaggedOnlyToggle = () => {
    setShowFlaggedOnly(!showFlaggedOnly);
  };

  const categories = reportCard
    ? Array.from(reportCard.keys())
    : ([] as string[]);

  let selectedCount = 0;
  itemSelected.forEach((selected: boolean) => {
    if (selected) selectedCount += 1;
  });

  let flaggedCount = 0;
  itemFlagged.forEach((flagged: boolean) => {
    if (flagged) flaggedCount += 1;
  });

  console.log("[ReportCardService] reportCardItems: ", reportCardItems);
  // console.log("[ReportCardService] reportCard: ", reportCard)
  // console.log("[ReportCardService] categoriesOpen: ", categoryOpen)
  // console.log("[ReportCardService] currentReportCardItem: ", currentReportCardItem)
  console.log("[ReportCardService] categorySelected: ", categorySelected);
  console.log("[ReportCardService] itemSelected: ", itemSelected);

  return (
    <div id="collection-report-card">
      <div className="collection-report-card-header">
        <div className="page-title">Report Card</div>
        <div className="collection-report-section-buttons">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleUpdateAll(true)}>
            Select All
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleUpdateAll(false)}>
            Unselect All
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleShowFlaggedOnlyToggle}
            disabled={flaggedCount < 1}>
            {showFlaggedOnly ? "Show All" : "Show Flagged"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleRun}
            disabled={selectedCount < 1}>
            Run
          </Button>
          
          {/* <button
            className="collection-report-card-button"
            onClick={() => handleUpdateAll(true)}>
            Select All
          </button>
          <button
            className="collection-report-card-button"
            onClick={() => handleUpdateAll(false)}>
            Unselect All
          </button>
          <button
            className="collection-report-card-button"
            onClick={handleShowFlaggedOnlyToggle}
            disabled={flaggedCount < 1}>
            {showFlaggedOnly ? "Show All" : "Show Flagged"}
          </button>
          <button
            className="collection-report-card-button"
            onClick={handleRun}
            disabled={selectedCount < 1}>
            Run
          </button> */}
        </div>
      </div>

      <div className="collection-report-card-split">
        <div className="collection-report-card-questions">
          <div className="collection-report-section-header">
            <div className="collection-report-section-title">{}</div>
          </div>
          {reportCard &&
            categoryOpen &&
            categories &&
            categories.length > 0 &&
            categories.map((category: string) => {
              const categoryItems = reportCard.get(category);
              const isOpen = categoryOpen.get(category);
              const isSelected = categorySelected?.get(category);

              return (
                <>
                  <div className="report-card-category">
                    <div className="report-card-category-accordion">
                      <img
                        src={
                          isOpen
                            ? iconPath("accordion_open.svg", storeUser)
                            : iconPath("accordion_closed.svg", storeUser)
                        }
                        onClick={() => {
                          const newCategoryOpen = new Map(categoryOpen);
                          newCategoryOpen.set(category, !isOpen);
                          setCategoryOpen(newCategoryOpen);
                        }}
                      />
                    </div>
                    <div className="report-card-category-select">
                      <img
                        src={isSelected ? "/check_on.svg" : "/check_off.svg"}
                        onClick={() => handleCategorySelect(category)}
                      />
                    </div>
                    <div className="report-card-category-name">{category}</div>
                  </div>

                  {categoryItems &&
                    categoryItems.length > 0 &&
                    isOpen &&
                    reportCardItems &&
                    categoryItems.map((reportCardItem: ReportCardItem) => {
                      const rciIndex = reportCardItems.findIndex(
                        (rci: ReportCardItem) => {
                          return rci.question === reportCardItem.question;
                        }
                      );

                      const isSelected =
                        rciIndex !== -1 ? itemSelected[rciIndex] : false;
                      const isFlagged =
                        rciIndex !== -1 ? itemFlagged[rciIndex] : false;

                      return (
                        <>
                          {(!showFlaggedOnly ||
                            (showFlaggedOnly && isFlagged)) && (
                            <div className="report-card-item">
                              <div
                                className="report-card-item-flagged"
                                onClick={() => handleItemFlag(rciIndex)}>
                                {
                                  isFlagged && (
                                    // <div className="report-card-item-reveal">
                                    <img src={"/item_flagged_on.svg"} />
                                  )
                                  // </div>
                                }
                                {!isFlagged && (
                                  <div
                                    className="report-card-item-reveal"
                                    // style={{display: "none"}}
                                  >
                                    <img src={"/item_flagged_off.svg"} />
                                  </div>
                                )}
                              </div>
                              <div className="report-card-item-select">
                                <img
                                  src={
                                    isSelected
                                      ? "/check_on.svg"
                                      : "/check_off.svg"
                                  }
                                  onClick={() => handleItemSelect(rciIndex)}
                                />
                              </div>
                              <div
                                className="report-card-item-question"
                                onClick={() =>
                                  setCurrentReportCardItem(reportCardItem)
                                }>
                                {reportCardItem.question}
                              </div>
                              <div className="report-card-item-success">
                                {reportCardItem.success !== null && (
                                  <img
                                    src={
                                      reportCardItem.success === false
                                        ? "/success_false.png"
                                        : "/success_true.png"
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })}
                </>
              );
            })}
          {/* {!reportCardItems &&
                    <>
                        Make a report report card 
                        <a onClick={handleCreateReportCard}>here</a>
                    </>

                    } */}
        </div>
        <div className="collection-report-card-answers">
          {currentReportCardItem && (
            <>
              <div className="report-card-question">
                {currentReportCardItem.question}
              </div>

              <div className="report-card-answer">
                {currentReportCardItem.answer
                  ? currentReportCardItem.answer
                  : "No answer yet"}
              </div>
              {currentReportCardItem.sources.length > 0 && (
                <div className="report-card-docs">
                  {currentReportCardItem.sources.length} sources
                  {/* {currentReportCardItem.sources && currentReportCardItem.sources.length > 0 &&
                                        currentReportCardItem.sources.map((linkRecord: LinkRecord) => {
                                            return (
                                                <LinkRecordActivity
                                                    linkRecord={linkRecord}
                                                    minimum={true}
                                                    showDrag={false}
                                                /> 
                                            );
                                        })
                                    } */}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showNoResources && (
        <ModalNoResources
          isOpen={showNoResources}
          onClose={() => setShowNoResources(false)}
          onContinue={() => navigate("/create")}
        />
      )}
    </div>
  );
};
